import { registerCustomModulePaths } from '@platform/core/lib/modulePaths';
import { dao } from '@platform/dao';
import type { ICatalog, IModuleDefinition, IRecordTypeResponse } from '@platform/core/types';
import { registerCustomModules } from '@platform/navigation/customModules';
import type { IModuleArg } from './args/IModuleArg';
import { baseApi, run } from './baseApi';
import { catalogTag } from './catalogTag';
import { customModuleEntries } from './customModuleEntries';
import { moduleTag } from './moduleTag';

/** Каталог простору; його власні модулі (ADR-025) реєструються в моделі навігації й адресах модулів (ADR-028) до того, як дані потраплять у кеш і перерендер. */
async function catalogOf(workspaceId: string | undefined): Promise<ICatalog> {
  const catalog = await dao.metadata.catalog(workspaceId);
  if (workspaceId) {
    const custom = customModuleEntries(catalog);
    registerCustomModules(workspaceId, custom);
    registerCustomModulePaths(workspaceId, custom);
  }
  return catalog;
}

/**
 * Метадані (ADR-023): каталог, визначення модуля й типу запису — злиті з налаштуваннями простору `workspaceId` (кеш окремо на кожен простір;
 * каталог простору містить і його власні модулі ADR-025),
 * без простору — опубліковані. Кеш — година; збереження налаштувань типу запису інвалідовує тег `Module` свого простору й модуля.
 */
export const metadataApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    getCatalog: b.query<ICatalog, { workspaceId?: string }>({
      queryFn: ({ workspaceId }) => run(() => catalogOf(workspaceId)),
      providesTags: (_r, _e, { workspaceId }) => ['Catalog', catalogTag(workspaceId)],
      keepUnusedDataFor: 3600,
    }),
    getModule: b.query<IModuleDefinition, IModuleArg>({
      queryFn: ({ key, workspaceId }) => run(() => dao.metadata.module(key, workspaceId)),
      providesTags: (_r, _e, { key, workspaceId }) => [moduleTag(workspaceId, key)],
      keepUnusedDataFor: 3600,
    }),
    getRecordType: b.query<IRecordTypeResponse, IModuleArg>({
      queryFn: ({ key, workspaceId }) => run(() => dao.metadata.recordType(key, workspaceId)),
      providesTags: (r, _e, { workspaceId }) => (r ? [moduleTag(workspaceId, r.module.module.key)] : []),
      keepUnusedDataFor: 3600,
    }),
  }),
});

export const { useGetCatalogQuery, useGetModuleQuery } = metadataApi;
